import React, { useState, useCallback } from "react";
import HeaderComponent from "@components/common/Header/Header";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import EditIcon from "@styles/all-images/icons/edit.svg";
import LockIcon from "@styles/all-images/icons/lock.svg";
import PlusIcon from "@styles/all-images/icons/plus.svg";
import CustomTable from "@/components/common/table/CustomTable";
import dateHandler from "@/composables/dateUtil";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import StaffManagementEditCard from "./StaffManagementEditCard";
import StaffAdd from "./StaffAdd";
import {
  ProfileApprovalStatus,
  ProfileApprovalStatusText,
  getStatusStyle,
} from "@/constants/enums/approval-status.enum";
import { useNavigate } from "react-router-dom";

interface StaffData {
  id: number;
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
  user_type_id: number;
  user_type: string;
  profile_picture: string | null;
  is_active: boolean;
  approval_status: number;
  accept_tc: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

const StaffManagement = () => {
  const { toStandardDisplayDate } = dateHandler();
  const dispatch = useDispatch<AppThunkDispatch>();
  const [forceRefresh, setForceRefresh] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<any>(null);
  const navigate = useNavigate();

  // Define columns for the CustomTable
  const columns = [
    {
      header: "User Name",
      accessor: (row: any) => `${row.first_name} ${row.last_name}`,
      sortable: true,
      sortKey: "first_name",
    },
    {
      header: "Email",
      accessor: "email",
      sortable: true,
      sortKey: "email",
    },
    {
      header: "Contact Number",
      accessor: "phone",
      sortable: true,
      sortKey: "phone",
    },
    {
      header: "Assigned Profile",
      accessor: (row: any) =>
        row.profiles?.length > 0 ? (
          <div className="tw-flex tw-items-center tw-gap-2">
            {row.profiles?.map((profile: string, i: number) => (
              <span
                key={i}
                className="tw-px-2 tw-bg-[#F8FAFC] tw-py-1 tw-border tw-border-[#E9EAEB] tw-rounded-full tw-text-sm tw-text-[#232323]"
              >
                {profile}
              </span>
            ))}
            <button onClick={() => handleEditProfile(row)}>
              <img src={EditIcon} alt="Edit Icon" className="tw-w-5 tw-h-5" />
            </button>
          </div>
        ) : (
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-px-2 tw-bg-[#F8FAFC] tw-py-1 tw-border tw-border-[#E9EAEB] tw-rounded-full tw-text-sm tw-text-[#232323]">
              No Profile Assigned
            </span>
          </div>
        ),
    },
    {
      header: "Status",
      accessor: (row: any) => {
        const status = row.approval_status as ProfileApprovalStatus;
        const { bgColor, textColor } = getStatusStyle(status);

        return (
          <li
            className={`tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-text-xs tw-w-fit ${bgColor} ${textColor}`}
          >
            <span className="tw-bg-[#717680] tw-w-2 tw-h-2 tw-rounded-full tw-p-1"></span>
            {ProfileApprovalStatusText[status]}
          </li>
        );
      },
      sortable: true,
      sortKey: "approval_status",
    },
    {
      header: "Created On",
      accessor: (row: any) =>
        toStandardDisplayDate({
          inputDate: row.created_at,
          inputFormat: "DD-MM-YYYY HH:mm:ss",
          desiredFormat: "dateTime",
        }),
      sortable: true,
      sortKey: "created_at",
    },
    {
      header: "Created By",
      accessor: "created_by",
      sortable: true,
      sortKey: "created_by",
    },
    {
      header: "Action",
      accessor: (row: any) => (
        <div className="tw-flex tw-items-center tw-gap-5">
          <button>
            <img
              src={LockIcon}
              alt="Lock Icon"
              className="tw-w-5 tw-h-5"
              onClick={() => {
                //handleStatusToggle(row)
              }}
            />
          </button>
        </div>
      ),
    },
  ];

  const handleFetch = useCallback(
    async (params: {
      page: number;
      limit: number;
      search?: string;
      sortColumn?: string;
      sortDirection?: "asc" | "desc";
    }) => {
      try {
        const { getAllData } = actionsHandler("staffList");
        const response = await dispatch(
          getAllData({
            limit: params.limit || 10,
            page: params.page || 1,
            search: params.search || "",
          }),
        );
        return response;
      } catch (error) {
        console.error("Error fetching staff data:", error);
        return {};
      }
    },
    [dispatch],
  );

  const handleEditProfile = (staff: any) => {
    setSelectedStaff(staff);
    setIsModalOpen(true);
  };

  const handleAddStaff = () => {
    setIsStaffModalOpen(true);
  };

  const handleCloseStaffModal = () => {
    setIsStaffModalOpen(false);
    setForceRefresh(true);
  };

  const handleCloseEditModal = () => {
    setIsModalOpen(false);
    setForceRefresh(true);
  };

  const handleStatusToggle = async (staff: StaffData) => {
    try {
      const { updateData } = actionsHandler("staffStatusToggle");
      await dispatch(
        updateData({
          id: staff.id,
          data: { is_active: !staff.is_active },
        }),
      );
      setForceRefresh(true);
    } catch (error) {
      console.error("Error toggling staff status:", error);
    }
  };

  return (
    <div className="tw-relative">
      <HeaderComponent />
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <img
                src={BackIcon}
                alt="Back Icon"
                onClick={() => navigate("/dashboard")}
                className="tw-cursor-pointer"
              />
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                Staff Management
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="tw-p-6 tw-mt-[147px]">
        <CustomTable
          columns={columns}
          fetchData={handleFetch}
          addButton={{
            text: "Add User",
            icon: PlusIcon,
            onClick: handleAddStaff,
          }}
          forceRefresh={forceRefresh}
          setForceRefresh={setForceRefresh}
        />

        {/* Modals */}
        {isModalOpen && (
          <StaffManagementEditCard
            onClose={handleCloseEditModal}
            onSubmit={handleCloseEditModal}
          />
        )}

        <div
          className={`tw-fixed tw-top-0 tw-right-0 tw-h-full tw-w-[310px] tw-bg-white tw-shadow-lg tw-transition-transform tw-duration-300 tw-ease-in-out ${
            isStaffModalOpen
              ? "tw-transform tw-translate-x-0"
              : "tw-transform tw-translate-x-full"
          } tw-z-50`}
        >
          {isStaffModalOpen && (
            <StaffAdd
              onClose={handleCloseStaffModal}
              onSubmit={handleCloseStaffModal}
              isStaffModalOpen={isStaffModalOpen}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffManagement;
